import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import store from '@/store';
import { sessionService } from '@kasasa/kloans-common-ui/dist/consumer-app/services/session';
import { amplifyService } from '@kasasa/kloans-common-ui/dist/consumer-app/services/amplify';
import { getKcasEnrollmentValidateToken } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication';
import { getCallbackUrl } from '@kasasa/kloans-common-ui/dist/utils/callback';

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
	{
		path: '/',
		name: 'login',
		component: () => import('@/views/Login/Login.vue'),
		meta: {
			title: 'Welcome To Kasasa',
			headline: 'Log in with your Kasasa account.',
			redirectOnAuth: true,
		},
		props: (route) => ({
			profileConnectionToken: route.query.token,
		}),
	},
	{
		path: '/sso',
		name: 'sso',
		component: () => import('@/views/Sso.vue'),
		meta: {
			title: 'Kasasa SSO',
		},
		props: (route) => ({
			code: route.query.code,
		}),
	},
	{
		path: '/login',
		redirect: '/',
	},
	{
		path: '/verify',
		name: 'mfa',
		component: () => import('@/views/MFA/MFA.vue'),
		meta: {
			title: 'Two-Factor Authentication',
			headline: 'Two-Factor Authentication',
			redirectOnAuth: true,
		},
		props: (route) => ({
			profileConnectionToken: route.query.token,
		}),
	},
	{
		path: '/forgot-username',
		name: 'forgotUsername',
		component: () => import('@/views/ForgotUsername/ForgotUsername.vue'),
		meta: {
			title: 'Forgot your username?',
			headline: 'Forgot your username?',
			redirectOnAuth: true,
		},
	},
	{
		path: '/username-reminder',
		name: 'usernameReminder',
		component: () => import('@/views/Static/Static.vue'),
		meta: {
			title: 'Check your email.',
			headline: 'Check your email.',
			staticData: {
				message: 'If we found a matching account, you will receive an email from us with your Kasasa username.',
				buttonLabel: 'Log In',
				buttonRoute: 'login',
			},
		},
	},
	{
		path: '/forgot-password',
		name: 'forgotPassword',
		component: () => import('@/views/ForgotPassword/ForgotPassword.vue'),
		meta: {
			title: 'Forgot your password?',
			headline: 'Forgot your password?',
			redirectOnAuth: true,
		},
	},
	{
		path: '/forgot-password-email-sent',
		name: 'forgotPasswordEmailSent',
		component: () => import('@/views/Static/Static.vue'),
		meta: {
			title: 'Forgot your password?',
			headline: 'Forgot your password?',
			staticData: {
				message: 'Check your mobile device. If your username was found, we texted you a code you can use to reset your password.',
				buttonLabel: 'Reset Password',
				buttonRoute: 'resetPassword',
			},
		},
	},
	{
		path: '/password-changed',
		name: 'passwordChanged',
		component: () => import('@/views/Static/Static.vue'),
		meta: {
			title: 'Password Changed',
			headline: 'Your password was successfully changed.',
			staticData: {
				message: 'You can now login with your username and your new password.',
				buttonLabel: 'Log In',
				buttonRoute: 'login',
			},
		},
	},
	{
		path: '/reset-password',
		name: 'resetPassword',
		component: () => import('@/views/ResetPassword/ResetPassword.vue'),
		meta: {
			title: 'Reset Password',
			headline: 'Reset Password',
		},
	},
	{
		path: '/password-reset',
		name: 'passwordReset',
		component: () => import('@/views/Static/Static.vue'),
		meta: {
			title: 'Password reset.',
			headline: 'Password reset.',
			staticData: {
				message: 'Your password has been updated.',
				buttonLabel: 'Log In',
				buttonRoute: 'login',
			},
		},
	},
	{
		path: '/account-locked',
		name: 'accountLocked',
		component: () => import('@/views/Static/Static.vue'),
		meta: {
			title: 'Account Locked',
			headline: 'Your account has been temporarily locked.',
			staticData: {
				message: 'Please contact your Kasasa institution for assistance.',
				buttonLabel: '',
				buttonRoute: '',
			},
		},
	},
	{
		path: '/logout',
		name: 'logout',
		component: () => import('@/views/Static/Static.vue'),
		meta: {
			title: 'You have been logged out.',
			headline: 'You have been logged out.',
			staticData: {
				message: '',
				buttonLabel: 'Log In',
				buttonRoute: 'login',
			},
		},

	},
	{
		path: '/session-expired',
		name: 'sessionExpired',
		component: () => import('@/views/Static/Static.vue'),
		meta: {
			title: 'Your session has expired.',
			headline: 'Your session has expired.',
			staticData: {
				message: '',
				buttonLabel: 'Log In',
				buttonRoute: 'login',
			},
		},
	},
	{
		path: '/verify-email-change/:token',
		name: 'emailChangeVerification',
		component: () => import('@/views/EmailChangeVerification/EmailChangeVerification.vue'),
		meta: {
			title: 'Verify Email Address Change',
			headline: 'Verify Email Address Change',
			validateToken: true,
			tokenFailureRoute: 'emailChangeTokenExpiredMessage',
		},
		props: (route) => ({
			token: route.params.token,
			failureRoute: route.meta.tokenFailureRoute,
		}),
	},
	{
		path: '/email-change-link-expired',
		name: 'emailChangeTokenExpiredMessage',
		component: () => import('@/views/Static/Static.vue'),
		meta: {
			title: 'This link has expired.',
			headline: 'This link has expired.',
			staticData: {
				message: 'Have you already confirmed your email change? If not, you can login to restart the process.',
				buttonLabel: 'Log In',
				buttonRoute: 'login',
			},
		},
	},
	{
		path: '/loan-invite/:token', // Generic for loan and mortgage with token link
		name: 'kasasaLoanInviteVerification',
		component: () => import('@/views/ProductInvite/ProductInvite.vue'),
		meta: {
			validateToken: true,
			hideWell: true,
			tokenFailureRoute: 'inviteTokenExpiredMessage',
			hideConsumerLogin: true,
		},
		props: (route) => ({
			token: route.params.token,
			inviteType: typeof route.query.type === 'string' ? parseInt(route.query.type, 10) : null,
		}),
	},
	{
		path: '/welcome/mortgage', // User direct access without token
		name: 'kasasaMortgageInvite',
		component: () => import('@/views/ProductInvite/ProductInvite.vue'),
		meta: {
			redirectOnAuth: true,
			hideWell: true,
			hideConsumerLogin: true,
		},
	},
	{
		path: '/invitation-expired',
		name: 'inviteTokenExpiredMessage',
		component: () => import('@/views/Static/Static.vue'),
		meta: {
			title: 'Your dashboard setup link has expired.',
			headline: 'Your dashboard setup link has expired.',
			staticData: {
				message: 'Please contact your Kasasa institution for assistance.',
				buttonLabel: 'Already created a Kasasa login?',
				buttonRoute: 'login',
			},
		},
	},
	{
		path: '/create-login',
		name: 'createLogin',
		component: () => import('@/views/CreateLogin/CreateLogin.vue'),
		meta: {
			title: 'Create Kasasa Login',
			headline: 'First, create your Kasasa login.',
			hideWell: true,
			enrollmentStep: true,
		},
	},
	{
		path: '/verify-mobile-device',
		name: 'verifyMobileDevice',
		component: () => import('@/views/VerifyMobileDevice/VerifyMobileDevice.vue'),
		meta: {
			title: 'Verify Mobile Device',
			headline: 'Verify your mobile device.',
			hideWell: true,
			enrollmentStep: true,
		},
	},
	{
		path: '/pin',
		name: 'pinSetup',
		component: () => import('@/views/PinSetup/PinSetup.vue'),
		meta: {
			title: 'Create Support code',
			headline: 'Create a Support Code.',
			hideWell: true,
			enrollmentStep: true,
		},
	},
	{
		path: '/enrollment-complete',
		name: 'enrollmentComplete',
		component: () => import('@/views/EnrollmentComplete/EnrollmentComplete.vue'),
		meta: {
			title: 'Enrollment Complete',
			headline: 'Success!',
		},
	},
	{
		path: '/enrollment-session-expired',
		name: 'enrollmentSessionExpiredMessage',
		component: () => import('@/views/Static/Static.vue'),
		meta: {
			title: 'Your session has expired.',
			headline: 'Your session has expired.',
			staticData: {
				message: 'Please use the link included in your email<br/>from Kasasa to restart enrollment.',
				buttonLabel: '',
				buttonRoute: '',
			},
		},
	},
	{
		path: '/terms-of-use',
		name: 'termsOfUse',
		component: () => import('@/views/Terms/Terms.vue'),
		meta: {
			title: 'Terms Of Use',
			headline: 'Terms Of Use',
		},
	},
	{
		path: '*',
		component: () => import('@/views/Static/Static.vue'),
		meta: {
			title: 'Page Not Found',
			headline: 'Page Not Found',
			staticData: {
				message: "Sorry, there's no page here!",
				buttonLabel: 'Log In',
				buttonRoute: 'login',
			},
		},
	},
];

const router = new VueRouter({
	base: '/login',
	routes,
});

router.beforeEach((to, from, next) => {
	if (store.state.errorText) {
		store.commit('setErrorText', '');
		store.commit('setHasErrorState', false);
	}

	if (to.meta.hideConsumerLogin) {
		store.commit('setConsumerLoginState', true);
	} else {
		store.commit('setConsumerLoginState', false);
	}

	if (to.matched.some((record) => record.meta.title)) {
		store.dispatch('updateTitle', to.meta.title);
	} else {
		store.dispatch('updateTitle', 'Welcome to Kasasa.');
	}

	if (to.matched.some((record) => record.meta.headline)) {
		store.commit('setHeadline', to.meta.headline);
	}

	if (to.meta.hideWell) {
		store.commit('setWellState', false);
	} else {
		store.commit('setWellState', true);
	}

	if (to.name === 'enrollmentSessionExpiredMessage') {
		sessionStorage.removeItem('token');
		sessionStorage.removeItem('last4Ssn');
		sessionStorage.removeItem('dateOfBirth');
	}

	if (to.meta.enrollmentStep) {
		const token: string | boolean = sessionStorage.getItem('token') || false;
		const last4Ssn: string | boolean = sessionStorage.getItem('last4Ssn') || false;
		const dateOfBirth: string | boolean = sessionStorage.getItem('dateOfBirth') || false;

		if (!token || !last4Ssn || !dateOfBirth) {
			setTimeout(() => {
				next({ name: 'enrollmentSessionExpiredMessage' });
			}, 5000);
		} else {
			next();
		}
	} else {
		next();
	}
});

router.beforeResolve(async (to, from, next) => {
	if (to.name === 'logout') {
		sessionService.logout(true);
		amplifyService.signOut();
	}

	if (to.meta.validateToken) {
		try {
			await getKcasEnrollmentValidateToken({
				token: to.params.token,
			});
		} catch {
			await next({
				name: to.meta.tokenFailureRoute,
			});

			return;
		}
	}

	if (to.matched.some((record) => record.meta.redirectOnAuth)) {
		if (sessionService.isAuthenticated()) {
			window.location.href = getCallbackUrl();
		}
	}

	next();
});

export default router;
